import React, { createContext, useContext, useState, ReactNode, useEffect } from 'react';
import { trpc } from '@org/clients/school';
import { toast } from 'sonner';

interface ChatContextType {
  isOpen: boolean;
  toggleChat: () => void;
  pageContext: string | undefined;
  setPageContext: (context: string | undefined) => void;
  sessionId: string | null;
  isLoading: boolean;
  startSession: () => void;
}

const ChatContext = createContext<ChatContextType | undefined>(undefined);

export function ChatProvider({ children }: { children: ReactNode }) {
  const [isOpen, setIsOpen] = useState(false);
  const [pageContext, setPageContext] = useState<string | undefined>(undefined);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [hasAttemptedSession, setHasAttemptedSession] = useState(false);
  
  const startSessionMutation = trpc.chat.startSession.useMutation();
  const sessionsQuery = trpc.chat.getSessions.useQuery(undefined, {
    enabled: isOpen && !sessionId,
    refetchOnWindowFocus: false
  });

  const toggleChat = () =>
    setIsOpen((prev) => {
      const next = !prev;
      if (next) {
        setHasAttemptedSession(false);
      }
      return next;
    });

  const startSession = async () => {
    try {
      const session = await startSessionMutation.mutateAsync({ title: 'New Chat' });
      setSessionId(session.sessionId);
    } catch (error) {
      console.error('Failed to start session:', error);
      toast.error('Failed to start chat session');
    }
  };

  // Manage session initialization
  useEffect(() => {
    if (sessionsQuery.isLoading) return;

    if (sessionsQuery.data && sessionsQuery.data.length > 0) {
      if (!sessionId) {
        setSessionId(sessionsQuery.data[0].sessionId);
      }
    } else if (
      isOpen &&
      !sessionId &&
      !sessionsQuery.isFetching &&
      !startSessionMutation.isPending &&
      !hasAttemptedSession
    ) {
      setHasAttemptedSession(true);
      void startSession();
    }
  }, [
    sessionsQuery.data,
    sessionsQuery.isLoading,
    sessionsQuery.isFetching,
    isOpen,
    sessionId,
    startSessionMutation.isPending,
    hasAttemptedSession,
  ]);

  return (
    <ChatContext.Provider value={{
      isOpen,
      toggleChat,
      pageContext,
      setPageContext,
      sessionId,
      isLoading: startSessionMutation.isPending || sessionsQuery.isLoading,
      startSession
    }}>
      {children}
    </ChatContext.Provider>
  );
}

export function useChat() {
  const context = useContext(ChatContext);
  if (context === undefined) {
    throw new Error('useChat must be used within a ChatProvider');
  }
  return context;
}

// Hook for pages to register context
export function usePageContext(context: string) {
  const { setPageContext } = useChat();
  
  useEffect(() => {
    setPageContext(context);
    return () => setPageContext(undefined);
  }, [context, setPageContext]);
}

